import { BaseService } from './BaseService';
import type {
  WebAutomationSiteDto,
  WebAutomationSiteTaskDto,
  WebAutomationScriptDto,
  WebAutomationRunDto,
  CreateWebAutomationSiteRequest,
  UpdateWebAutomationSiteRequest,
  ValidateWebAutomationRunRequest,
  CreateWebAutomationRunRequest,
} from '../types/webAutomation';

/**
 * Service for the Web Automation feature (Module 36):
 * sites, site-agent attachments (SiteAgents), scripts and runs.
 */
export class WebAutomationService extends BaseService {
  // --- Sites ---

  async getSites(isActive?: boolean): Promise<WebAutomationSiteDto[]> {
    const query = isActive !== undefined ? `?isActive=${isActive}` : '';
    return this.get<WebAutomationSiteDto[]>(`/web-automation/sites${query}`);
  }

  async getSite(id: string): Promise<WebAutomationSiteDto> {
    return this.get<WebAutomationSiteDto>(`/web-automation/sites/${id}`);
  }

  async createSite(data: CreateWebAutomationSiteRequest): Promise<WebAutomationSiteDto> {
    return this.post<WebAutomationSiteDto>('/web-automation/sites', data);
  }

  async updateSite(id: string, data: UpdateWebAutomationSiteRequest): Promise<WebAutomationSiteDto> {
    return this.put<WebAutomationSiteDto>(`/web-automation/sites/${id}`, data);
  }

  async deleteSite(id: string): Promise<void> {
    return this.delete<void>(`/web-automation/sites/${id}`);
  }

  // --- Site agents (attach = executable unit) ---

  async getSiteTasks(siteId: string): Promise<WebAutomationSiteTaskDto[]> {
    return this.get<WebAutomationSiteTaskDto[]>(`/web-automation/sites/${siteId}/agents`);
  }

  async attachAgent(siteId: string, agentDefinitionId: string): Promise<WebAutomationSiteTaskDto> {
    return this.post<WebAutomationSiteTaskDto>(`/web-automation/sites/${siteId}/agents`, {
      agentDefinitionId,
    });
  }

  async detachAgent(siteId: string, siteTaskId: string): Promise<void> {
    return this.delete<void>(`/web-automation/sites/${siteId}/agents/${siteTaskId}`);
  }

  async reorderSiteTasks(siteId: string, orderedSiteTaskIds: string[]): Promise<void> {
    return this.put<void>(`/web-automation/sites/${siteId}/agents/reorder`, { orderedSiteTaskIds });
  }

  // --- Scripts ---

  async getCachedScript(siteTaskId: string): Promise<WebAutomationScriptDto | null> {
    // 204 No Content means no cached script exists yet.
    const response = await this.client.get(`/web-automation/scripts/cached/${siteTaskId}`);
    return response.status === 204 ? null : (response.data as WebAutomationScriptDto);
  }

  async invalidateScriptCache(siteTaskId: string): Promise<void> {
    return this.post<void>(`/web-automation/scripts/invalidate/${siteTaskId}`);
  }

  /**
   * Saves an edited script as the new current version for a site task.
   * The API validates the Rich DSL structure and rejects invalid scripts.
   */
  async saveScript(
    siteTaskId: string,
    dslJson: string,
    generatedBy = 'manual',
    notes?: string,
  ): Promise<WebAutomationScriptDto> {
    return this.post<WebAutomationScriptDto>('/web-automation/scripts', {
      siteTaskId,
      dslJson,
      generatedBy,
      notes,
    });
  }

  /** JSON Schema for the Rich script DSL (drives Monaco diagnostics/completion). */
  async getDslSchema(): Promise<object> {
    return this.get<object>('/web-automation/scripts/dsl-schema');
  }

  // --- Runs ---

  async getRuns(siteTaskId?: string, status?: string, limit = 100): Promise<WebAutomationRunDto[]> {
    const params = new URLSearchParams();
    if (siteTaskId) params.append('siteTaskId', siteTaskId);
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    return this.get<WebAutomationRunDto[]>(`/web-automation/runs?${params.toString()}`);
  }

  /** Fetches a single run — used by the manual Refresh button and dialog open (activity log, phase, heartbeat). */
  async getRun(runId: string): Promise<WebAutomationRunDto> {
    return this.get<WebAutomationRunDto>(`/web-automation/runs/${runId}`);
  }

  async validateRun(runId: string, data: ValidateWebAutomationRunRequest): Promise<void> {
    return this.put<void>(`/web-automation/runs/${runId}/validate`, data);
  }

  /** Queues a Pending run that the desktop runner picks up and executes. */
  async createRun(data: CreateWebAutomationRunRequest): Promise<WebAutomationRunDto> {
    return this.post<WebAutomationRunDto>('/web-automation/runs', data);
  }
}

export type {
  WebAutomationSiteDto,
  WebAutomationSiteTaskDto,
  WebAutomationScriptDto,
  WebAutomationRunDto,
  CreateWebAutomationSiteRequest,
  UpdateWebAutomationSiteRequest,
  ValidateWebAutomationRunRequest,
  CreateWebAutomationRunRequest,
} from '../types/webAutomation';

export const webAutomationService = new WebAutomationService();
